/**
 * « Pas encore vu en classe » — le seul refus que l'enfant ait le droit d'opposer.
 *
 * Hedi, le 7 septembre 2026 : *« Si des compétences sont ouvertes sans être vues
 * dans les inputs, est-ce qu'il n'y a pas de risque de proposer des exercices
 * non vus ? […] Ça a du sens d'avoir un bouton : pas encore vu pour les
 * enfants ? »* Oui, et le risque est mesuré : la frontière du programme retombe
 * en dernier recours sur un découpage théorique de l'année que personne n'a
 * signé — 13 des 62 compétences de philosophie ouvertes à Mehdi ce jour-là ne
 * tenaient qu'à lui — et la bande de difficulté du soir, elle, ne s'adapte pas.
 *
 * **Discret, et jamais à côté du bouton « Envoyer ».** Il est rangé sous
 * l'exercice, en retrait : une porte de sortie posée à hauteur de la réponse
 * devient la réponse. Ce n'est pas « je ne sais pas faire » — pour cela il y a
 * les indices et le corrigé —, c'est « la classe n'y est pas encore allée », et
 * les deux phrases ne se disent pas au même moment.
 *
 * **Il demande confirmation en une étape et une seule.** Un appui accidentel
 * retire un chapitre pour deux semaines ; trois écrans de confirmation, et
 * personne ne s'en sert.
 *
 * Il vit dans `ui/` et non dans `child/` parce que le refus est le même geste
 * partout où un exercice est servi, et parce que la coquille enfant n'a pas le
 * droit d'importer `parent/` — la règle que `ui/periode.ts` a déjà appliquée.
 */

import { useState } from "react";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { PasEncoreVuOut } from "@/api/schema";
import { enClair } from "@/ui/dates";
import { Button, ErrorNote } from "@/ui/primitives";

type Etat = "ferme" | "confirme" | "envoi" | "fait" | "refuse" | "panne";

/**
 * Ce que l'enfant lit une fois le chapitre retiré.
 *
 * Séparé du composant pour être testable sans DOM, comme `consigne` dans
 * `RenduAuParent`. La phrase nomme le chapitre : « c'est noté » tout seul ne
 * dit pas *quoi*, et l'enfant ne peut alors pas vérifier qu'il a appuyé sur ce
 * qu'il croyait.
 */
export function confirmation(rendu: PasEncoreVuOut): string {
  const chapitre = rendu.libelle || "Ce chapitre";
  return `C'est noté : « ${chapitre} » ne te sera plus proposé jusqu'au ${enClair(
    rendu.jusqu_au,
  )}. Tes parents sont prévenus.`;
}

export function PasEncoreVu({ exerciseId }: { exerciseId: string }) {
  const [etat, setEtat] = useState<Etat>("ferme");
  const [rendu, setRendu] = useState<PasEncoreVuOut | null>(null);
  const [refus, setRefus] = useState("");

  const declarer = async () => {
    setEtat("envoi");
    try {
      const reponse = await call("pasEncoreVu", { params: { exercise_id: exerciseId } });
      setRendu(reponse);
      setEtat("fait");
    } catch (erreur) {
      // **Le refus n'est pas une panne, et se lit autrement.** Le serveur
      // refuse quand une source a prouvé que la classe a traité le chapitre, et
      // il dit laquelle — le taire ferait passer une règle pour un bug, après
      // quoi le bouton ne sert plus.
      if (erreur instanceof ApiError && erreur.status === 422) {
        setRefus(erreur.message);
        setEtat("refuse");
      } else {
        setEtat("panne");
      }
    }
  };

  if (etat === "fait" && rendu) {
    return <p className="pas-vu pas-vu--fait">{confirmation(rendu)}</p>;
  }

  return (
    <div className="pas-vu">
      {etat === "ferme" ? (
        <Button variant="quiet" onClick={() => setEtat("confirme")}>
          On n'a pas encore vu ça en classe
        </Button>
      ) : null}

      {etat === "confirme" || etat === "envoi" ? (
        <div className="pas-vu__confirmation">
          <p>Tu es sûr ? Ce chapitre ne te sera plus proposé pendant deux semaines.</p>
          <div className="pas-vu__actions">
            <Button onClick={declarer} disabled={etat === "envoi"}>
              {etat === "envoi" ? "Un instant…" : "Oui, on ne l'a pas vu"}
            </Button>
            <Button variant="quiet" onClick={() => setEtat("ferme")} disabled={etat === "envoi"}>
              Annuler
            </Button>
          </div>
        </div>
      ) : null}

      {etat === "refuse" ? <ErrorNote>{refus}</ErrorNote> : null}
      {etat === "panne" ? (
        <ErrorNote>Ça n'est pas parti. Réessaie dans un instant.</ErrorNote>
      ) : null}
    </div>
  );
}
